import {
    createRef,
    createSignal,
    ReadonlySignal,
    MutableSignal,
    SerializableInstance,
} from '@funtech-int/rival';

interface UpdateCtx {
    stage(): void;
    commit(): void;
    rollback(): void;
}

interface UpdateOperation<T> {
    (ctx: UpdateCtx): T;
}

export interface InMemoryCache {
    get<T>(this: void, key: string): T | undefined;
    get<T>(this: void, key: string, fallback: T): T;
    getSignal<T>(this: void, key: string): ReadonlySignal<T>;
    getSignal<T>(this: void, key: string, fallback?: T): ReadonlySignal<T>;
    set<T>(this: void, key: string, value: T): void;
    update<T, U>(this: void, key: string, value: T, op: UpdateOperation<U>): U;
}

function createDisposableSignal<T>(initialValue: T, onDispose: VoidFunction) {
    const signal = createSignal(initialValue);

    const { listen } = signal;

    signal.listen = (listener) => {
        const unlisten = listen(listener);
        return () => {
            unlisten();
            onDispose();
        };
    };

    return signal;
}

export const inMemoryCacheRef = createRef<InMemoryCache>('inMemoryCache');

export function createInMemoryCache(): SerializableInstance<InMemoryCache, Record<string, any>> {
    let cache = new Map<string, any>();
    const signals = new Map<string, MutableSignal<any>>();

    return {
        get(key, fallback?) {
            return cache.get(key) ?? fallback;
        },

        getSignal(key, fallback?) {
            let signal = signals.get(key);

            if (!signal) {
                signal = createDisposableSignal(cache.get(key) ?? fallback, () => {
                    Promise.resolve().then(() => {
                        if (signal?.refs() === 0) {
                            signals.delete(key);
                        }
                    });
                });
                signals.set(key, signal);
            }

            return signal;
        },

        set(key, value) {
            cache.set(key, value);

            signals.get(key)?.set(value);
        },

        update(key, value, op) {
            const signal = signals.get(key);

            const ctx = {
                stage() {
                    signal?.set(value);
                },
                commit() {
                    cache.set(key, value);
                },
                rollback() {
                    signal?.set(cache.get(key));
                },
            };

            return op(ctx);
        },

        ref() {
            return inMemoryCacheRef;
        },

        toJSON() {
            return Object.fromEntries(cache);
        },

        hydrate(data) {
            cache = new Map(Object.entries(data));
        },
    };
}
